import { useEffect, useRef, useState } from 'react'
import { cn } from '../../lib/cn.js'
import { Textarea } from './Textarea.js'
import { Button } from './Button.js'

export interface JsonEditorProps {
  /** The current value, as the API stores it. Any JSON. */
  value: unknown
  /** Fires only while the text is valid JSON. Invalid text is never emitted. */
  onValueChange: (value: unknown) => void
  /** Reports whether what is on screen can be saved, so a form can disable Save. */
  onValidityChange?: (valid: boolean) => void
  id?: string
  rows?: number
  disabled?: boolean
  className?: string
}

const show = (v: unknown) => {
  try {
    return JSON.stringify(v, null, 2) ?? ''
  } catch {
    return ''
  }
}

/**
 * The platform settings, which are not all the same shape.
 *
 * `PUT /v1/admin/settings/{key}` takes `value` as *anything*, and it means it: the
 * seventeen settings behind it are numbers, strings, booleans, a fixed set of words,
 * a lookup of category slugs, and — since the purpose statement — an object with two
 * paragraphs in it. There is no single control for that. A number box cannot hold
 * `{ "short": …, "long": … }`, and a text box would save the number `250` as the
 * string `"250"`, which the setting's own schema would then reject and fall back
 * from — silently, to the default, which is the worst possible outcome because the
 * screen would show the change as saved.
 *
 * So the desk edits the value as JSON, and this refuses to hand anything upwards
 * until it parses. Typing is never blocked, only saving: a control that fights you
 * mid-keystroke is a control nobody can use.
 */
export function JsonEditor({
  value,
  onValueChange,
  onValidityChange,
  id,
  rows = 8,
  disabled,
  className,
}: JsonEditorProps) {
  const [text, setText] = useState(() => show(value))
  const [error, setError] = useState<string | null>(null)

  /**
   * What this editor last sent upwards.
   *
   * Without it the component fights the person using it. Typing a valid character
   * calls `onValueChange`, the parent re-renders with the new value, and a naive
   * effect would then overwrite the text with the pretty-printed form of what was
   * just typed — reindenting and moving the cursor on every keystroke.
   *
   * So an echo of our own change is ignored, and only a value that came from
   * somewhere else (the row loading, or a different setting being opened) reseeds
   * the box.
   */
  const ours = useRef(show(value))

  useEffect(() => {
    const incoming = show(value)
    if (incoming === ours.current) return
    ours.current = incoming
    setText(incoming)
    setError(null)
  }, [value])

  const onText = (next: string) => {
    setText(next)
    try {
      const parsed: unknown = JSON.parse(next)
      setError(null)
      onValidityChange?.(true)
      ours.current = show(parsed)
      onValueChange(parsed)
    } catch (e) {
      // The parser's own message, kept verbatim — it says where the problem is.
      setError(e instanceof Error ? e.message : 'That is not valid JSON')
      onValidityChange?.(false)
    }
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <Textarea
        id={id}
        rows={rows}
        value={text}
        disabled={disabled}
        invalid={error !== null}
        spellCheck={false}
        className="font-mono text-second"
        onChange={(e) => onText(e.target.value)}
      />
      <div className="flex items-center justify-between gap-3">
        <span
          className={cn('text-caption', error ? 'text-error' : 'text-ink-muted')}
          aria-live="polite"
        >
          {/* The parser's own words. "Unexpected token } in JSON at position 41"
              tells somebody where to look; "invalid" tells them nothing. */}
          {error ?? 'Valid'}
        </span>
        <Button
          variant="tertiary"
          size="sm"
          disabled={disabled || error !== null}
          onClick={() => setText(show(JSON.parse(text)))}
        >
          Tidy up
        </Button>
      </div>
    </div>
  )
}
